import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { AlertTriangle, CalendarDays, Check, Loader2, Phone, X } from 'lucide-react';
import { cn, capitalize } from '../../lib/utils';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { ApprovalStatusBadge } from '../ApprovalStatusBadge';
import { MonthCalendar, TimesheetEntry } from '../../services/timesheetService';
import { OffDayKind, PersonMonthRow, formatHours, issuesByDay, monthKey, toDateKey } from './approvals.utils';
import { isOnCall } from '../../lib/timesheet-hours';
import { IssueList } from './TimesheetIssues';

/*
 * One person's month, in pieces: the summary lines, the day-by-day ledger with the review
 * checkboxes, and the decision bar. The approvals list shows them in a popup, the person page
 * shows them full width; both read the same row built from the management endpoint.
 */

const num = (value: unknown): number => Number(value) || 0;

export function useDetailLocale() {
  const { i18n } = useTranslation();
  return { locale: i18n.language?.startsWith('it') ? it : enUS, numberLocale: i18n.language || 'en' };
}

export function useEntriesLabel() {
  const { t } = useTranslation();
  return (count: number) =>
    count === 1 ? t('approvals.count.entryOne', '1 voce') : t('approvals.count.entryOther', '{{count}} voci', { count });
}

/**
 * Pending entries the reviewer is deciding on. Reviewing a person starts from "approve the whole
 * month"; unticking is the exception. Resets whenever the person or their pending entries change.
 */
export function usePendingSelection(row: PersonMonthRow | null, active = true) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const pendingKey = row?.pendingIds.join(',') ?? '';

  useEffect(() => {
    if (active && row) setSelected(new Set(row.pendingIds));
  }, [active, row?.userId, pendingKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingCount = row?.pendingIds.length ?? 0;
  const allSelected = pendingCount > 0 && selected.size === pendingCount;
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => setSelected(allSelected || !row ? new Set() : new Set(row.pendingIds));
  const toggleOne = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return { selected, allSelected, someSelected, toggleAll, toggleOne };
}

export type PendingSelection = ReturnType<typeof usePendingSelection>;

/** Amber "n da approvare" or green "Approvato". */
export function MonthStatusPill({ pendingCount }: { pendingCount: number }) {
  const { t } = useTranslation();
  const entriesLabel = useEntriesLabel();

  if (pendingCount > 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        {t('approvals.rowStatus.pending', '{{entries}} da approvare', { entries: entriesLabel(pendingCount) })}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
      <Check className="h-3 w-3" />
      {t('approvals.rowStatus.approved', 'Approvato')}
    </span>
  );
}

/**
 * The month's yardstick: "176,0 h attese · 22 giorni lavorativi · 1 festività · 2 giorni di chiusura aziendale".
 * Weekdays net of holidays and company closings, at the standard day; the same numbers the checks use.
 */
export function MonthCalendarLine({ calendar, className }: { calendar: MonthCalendar | null; className?: string }) {
  const { t } = useTranslation();
  const { numberLocale } = useDetailLocale();
  if (!calendar) return null;

  const holidays = calendar.holidayDates.length;
  const closings = calendar.closingDates.length;
  const parts = [
    calendar.workingDays === 1
      ? t('approvals.calendar.workingDaysOne', '1 giorno lavorativo')
      : t('approvals.calendar.workingDaysOther', '{{count}} giorni lavorativi', { count: calendar.workingDays }),
  ];
  if (holidays > 0) {
    parts.push(holidays === 1 ? t('approvals.calendar.holidaysOne', '1 festività') : t('approvals.calendar.holidaysOther', '{{count}} festività', { count: holidays }));
  }
  if (closings > 0) {
    parts.push(
      closings === 1
        ? t('approvals.calendar.closingDaysOne', '1 giorno di chiusura aziendale')
        : t('approvals.calendar.closingDaysOther', '{{count}} giorni di chiusura aziendale', { count: closings }),
    );
  }

  return (
    <p
      className={cn('flex flex-wrap items-center gap-x-1.5 text-sm tabular-nums', className)}
      title={t('approvals.calendar.hint', 'Giorni lavorativi del mese (lun–ven) esclusi festività e chiusure aziendali, per {{hours}} h al giorno', {
        hours: formatHours(calendar.standardDailyHours, numberLocale),
      })}
    >
      <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
      <span className="font-semibold text-foreground">
        {t('approvals.calendar.expectedHours', '{{hours}} h attese', { hours: formatHours(calendar.expectedHours, numberLocale) })}
      </span>
      <span className="text-muted-foreground">· {parts.join(' · ')}</span>
    </p>
  );
}

/** The month in two lines (hours, days) plus the consistency checks; with the calendar, hours and days read against the expected ones. */
export function MonthSummary({ row, calendar, className }: { row: PersonMonthRow; calendar?: MonthCalendar | null; className?: string }) {
  const { t } = useTranslation();
  const { numberLocale } = useDetailLocale();

  const hourStats = [
    { key: 'workedHours', label: t('approvals.columns.workedHours', 'Ore lavorate'), value: row.workedHours, expected: calendar?.expectedHours },
    { key: 'overtimeHours', label: t('approvals.columns.overtimeHours', 'Straordinari'), value: row.overtimeHours },
    { key: 'onCallHours', label: t('approvals.columns.onCallHours', 'Reperibilità'), value: row.onCallHours },
  ];

  // Days line: only what actually happened this month, so a plain month reads as one short sentence.
  const daysLine: string[] = [
    calendar
      ? t('approvals.detail.workedDaysOf', '{{count}} giorni lavorati su {{total}} lavorativi', { count: row.stats.workedDays, total: calendar.workingDays })
      : t('approvals.detail.workedDaysCount', '{{count}} giorni lavorati', { count: row.stats.workedDays }),
  ];
  if (row.stats.onCallDays > 0) {
    daysLine.push(t('approvals.detail.onCallDaysCount', '{{count}} giorni in reperibilità', { count: row.stats.onCallDays }));
  }
  const absences = [
    { label: t('approvals.stats.vacationDays', 'Ferie'), value: row.stats.vacationDays },
    { label: t('approvals.stats.permissionDays', 'Permesso'), value: row.stats.permissionDays },
    { label: t('approvals.stats.sicknessDays', 'Malattia'), value: row.stats.sicknessDays },
    { label: t('approvals.stats.rolDays', 'ROL'), value: row.stats.rolDays },
  ].filter((item) => item.value > 0);
  absences.forEach((item) => daysLine.push(`${item.label} ${item.value} ${t('common.days', 'giorni')}`));
  if (row.permitHours > 0) {
    daysLine.push(t('approvals.detail.permitHoursInline', 'Assenze {{hours}} h', { hours: formatHours(row.permitHours, numberLocale) }));
  }
  if (absences.length === 0 && row.permitHours === 0) {
    daysLine.push(t('approvals.detail.noAbsences', 'nessuna assenza'));
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="space-y-1 text-sm">
        <p className="flex flex-wrap gap-x-4 gap-y-1">
          {hourStats.map((stat) => (
            <span key={stat.key} className="text-muted-foreground">
              {stat.label}{' '}
              <span className={cn('font-semibold tabular-nums text-foreground', stat.value === 0 && 'text-muted-foreground/60')}>
                {formatHours(stat.value, numberLocale)}
              </span>
              {stat.expected !== undefined && (
                <span className="tabular-nums text-muted-foreground" title={t('approvals.detail.expectedHoursTitle', 'Ore attese nel mese')}>
                  {' / '}
                  {formatHours(stat.expected, numberLocale)}
                </span>
              )}
            </span>
          ))}
        </p>
        <p className="text-muted-foreground">{daysLine.join(' · ')}</p>
      </div>
      <IssueList issues={row.issues} />
    </div>
  );
}

/** Ledger cell: zeros recede so the values that matter stand out. */
function HoursCell({ value, locale, className }: { value: number; locale: string; className?: string }) {
  return (
    <td className={cn('px-2 py-2 text-right tabular-nums', value === 0 && 'text-muted-foreground/50', className)}>
      {formatHours(value, locale)}
    </td>
  );
}

/** On-call hours with a phone when the person was on call, so "0,0" reads as "on call, no call-outs". */
function OnCallCell({ entry, locale, className }: { entry: TimesheetEntry; locale: string; className?: string }) {
  const { t } = useTranslation();
  if (!isOnCall(entry)) return <HoursCell value={0} locale={locale} className={className} />;
  return (
    <td className={cn('px-2 py-2 text-right tabular-nums text-blue-700 dark:text-blue-300', className)}>
      <span className="inline-flex items-center gap-1" title={t('approvals.detail.onCall', 'Reperibile')}>
        <Phone className="h-3 w-3" aria-label={t('approvals.detail.onCall', 'Reperibile')} />
        {formatHours(num(entry.onCallHours), locale)}
      </span>
    </td>
  );
}

interface MonthLedgerProps {
  row: PersonMonthRow;
  selection: PendingSelection;
  /** With this, every calendar day gets a row, so a gap reads as a gap and not as nothing; `offDay` names holidays and company closings. */
  allDays?: { year: number; month: number; offDay?: (dateKey: string) => OffDayKind | undefined };
}

export function MonthLedger({ row, selection, allDays }: MonthLedgerProps) {
  const { t } = useTranslation();
  const { locale, numberLocale } = useDetailLocale();
  const { selected, allSelected, someSelected, toggleAll, toggleOne } = selection;
  const pendingCount = row.pendingIds.length;
  const dayIssues = useMemo(() => issuesByDay(row.issues), [row.issues]);
  const allYear = allDays?.year;
  const allMonth = allDays?.month;

  // Entries grouped per day (orders sorted by code); with `allDays` the empty days are listed too.
  const days = useMemo(() => {
    const byDay = new Map<string, TimesheetEntry[]>();
    for (const entry of row.entries) {
      const key = toDateKey(entry.date);
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key)!.push(entry);
    }
    byDay.forEach((list) => list.sort((a, b) => (a.order?.code ?? '').localeCompare(b.order?.code ?? '')));
    const keys =
      allYear && allMonth
        ? Array.from({ length: new Date(allYear, allMonth, 0).getDate() }, (_, index) => `${monthKey(allYear, allMonth)}-${String(index + 1).padStart(2, '0')}`)
        : Array.from(byDay.keys()).sort();
    return keys.map((dateKey) => ({ dateKey, entries: byDay.get(dateKey) ?? [] }));
  }, [row.entries, allYear, allMonth]);

  const headCell = 'px-2 py-2 font-semibold';

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground [&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-background [&>th]:shadow-[inset_0_-1px_0_hsl(var(--border))]">
          <th className={cn(headCell, 'w-8 px-1 text-left sm:px-2')}>
            {pendingCount > 0 && (
              <Checkbox
                aria-label={t('approvals.detail.selectAll', 'Seleziona tutte le voci da approvare')}
                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                onCheckedChange={toggleAll}
              />
            )}
          </th>
          <th className={cn(headCell, 'px-1 text-left sm:px-2')}>{t('approvals.detail.columns.day', 'Giorno')}</th>
          <th className={cn(headCell, 'px-1.5 text-left sm:px-2')}>{t('approvals.detail.columns.order', 'Ordine')}</th>
          <th className={cn(headCell, 'text-right')}>
            <span className="sm:hidden">{t('approvals.detail.columns.hoursShort', 'Ore')}</span>
            <span className="hidden sm:inline">{t('timesheet.columns.workedHours', 'Lavorate')}</span>
          </th>
          <th className={cn(headCell, 'hidden text-right sm:table-cell')}>{t('approvals.detail.columns.overtime', 'Straord.')}</th>
          <th className={cn(headCell, 'hidden text-right sm:table-cell')}>{t('approvals.detail.columns.onCall', 'Reper.')}</th>
          <th className={cn(headCell, 'hidden text-right sm:table-cell')}>{t('timesheet.columns.workPermit', 'Assenze')}</th>
          <th className={cn(headCell, 'text-right')}>
            <span className="sr-only sm:not-sr-only">{t('approvals.detail.columns.state', 'Stato')}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {days.map(({ dateKey, entries }) => {
          const date = parseISO(dateKey);
          const weekend = date.getDay() === 0 || date.getDay() === 6;
          const offDay = allDays?.offDay?.(dateKey);
          const holiday = offDay !== undefined;
          const issuesOfDay = dayIssues.get(dateKey) ?? [];
          const dayHasError = issuesOfDay.some((issue) => issue.severity === 'error');
          const issueTitle = issuesOfDay.map((issue) => t(`approvals.issues.codes.${issue.code}.label`, issue.code)).join(', ');
          const dayLabel = (
            <>
              {capitalize(format(date, 'EEE d', { locale }))}
              {issuesOfDay.length > 0 && (
                <AlertTriangle
                  aria-label={issueTitle}
                  className={cn('h-3.5 w-3.5', dayHasError ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400')}
                >
                  <title>{issueTitle}</title>
                </AlertTriangle>
              )}
            </>
          );

          if (entries.length === 0) {
            const reason = weekend
              ? t('approvals.strip.weekend', 'Weekend')
              : offDay === 'holiday'
                ? t('approvals.strip.holiday', 'Festività')
                : offDay === 'closing'
                  ? t('approvals.strip.closing', 'Chiusura aziendale')
                  : t('approvals.strip.noEntry', 'Nessuna voce');
            return (
              <tr key={dateKey} className={cn('border-t border-border/60', (weekend || holiday) && 'bg-muted/30')}>
                <td className="px-2 py-1.5" />
                <td className="whitespace-nowrap px-1 py-1.5 tabular-nums text-muted-foreground sm:px-2">
                  <span className="inline-flex items-center gap-1.5">{dayLabel}</span>
                </td>
                <td
                  colSpan={6}
                  className={cn('px-2 py-1.5 text-xs', issuesOfDay.length > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground/60')}
                >
                  {reason}
                </td>
              </tr>
            );
          }

          return entries.map((entry, index) => {
            const firstOfDay = index === 0;
            const canDecide = entry.state === 'in_approval';
            const isSelected = canDecide && selected.has(entry.id);

            return (
              <tr
                key={entry.id}
                className={cn(firstOfDay ? 'border-t border-border/60' : 'border-t-0', isSelected && 'bg-amber-50/70 dark:bg-amber-950/20')}
              >
                <td className="px-1 py-2 align-middle sm:px-2">
                  {canDecide && (
                    <Checkbox
                      aria-label={format(date, 'EEEE d MMMM', { locale })}
                      checked={isSelected}
                      onCheckedChange={() => toggleOne(entry.id)}
                    />
                  )}
                </td>
                <td className={cn('whitespace-nowrap px-1 py-2 tabular-nums sm:px-2', weekend && 'text-muted-foreground')}>
                  <span className={cn('inline-flex items-center gap-1.5', !firstOfDay && 'invisible')}>{dayLabel}</span>
                </td>
                <td className="max-w-[128px] px-1.5 py-2 sm:max-w-[220px] sm:px-2">
                  <span className="block truncate font-medium">{entry.order?.code || t('approvals.detail.noOrder', 'Nessun ordine')}</span>
                  {entry.order?.description && (
                    <span className="block truncate text-xs text-muted-foreground">{entry.order.description}</span>
                  )}
                  {/* Phone: the hidden columns fold under the order */}
                  {(num(entry.overtimeHours) > 0 || isOnCall(entry) || num(entry.workPermit) > 0) && (
                    <span className="block text-xs tabular-nums text-muted-foreground sm:hidden">
                      {[
                        num(entry.overtimeHours) > 0 && `${t('approvals.detail.columns.overtime', 'Straord.')} ${formatHours(num(entry.overtimeHours), numberLocale)}`,
                        isOnCall(entry) && `${t('approvals.detail.columns.onCall', 'Reper.')} ${formatHours(num(entry.onCallHours), numberLocale)}`,
                        num(entry.workPermit) > 0 && `${t('timesheet.columns.workPermit', 'Assenze')} ${formatHours(num(entry.workPermit), numberLocale)}`,
                      ].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </td>
                <HoursCell value={num(entry.workedHours)} locale={numberLocale} />
                <HoursCell value={num(entry.overtimeHours)} locale={numberLocale} className="hidden sm:table-cell" />
                <OnCallCell entry={entry} locale={numberLocale} className="hidden sm:table-cell" />
                <HoursCell value={num(entry.workPermit)} locale={numberLocale} className="hidden sm:table-cell" />
                <td className="px-2 py-2 text-right">
                  <ApprovalStatusBadge state={entry.state} rejectionReason={entry.rejectionReason} compact />
                </td>
              </tr>
            );
          });
        })}
      </tbody>
    </table>
  );
}

interface MonthDecisionBarProps {
  pendingCount: number;
  selectedCount: number;
  processing: boolean;
  onApprove: () => void;
  onReject: () => void;
  /** Left side of the bar: a link, a selection summary… */
  leading?: React.ReactNode;
  className?: string;
}

export function MonthDecisionBar({ pendingCount, selectedCount, processing, onApprove, onReject, leading, className }: MonthDecisionBarProps) {
  const { t } = useTranslation();
  const entriesLabel = useEntriesLabel();

  return (
    <div className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:items-center', className)}>
      {leading}
      <div className="flex-1" />
      {pendingCount === 0 ? (
        <span className="text-sm text-muted-foreground">
          {t('approvals.detail.allApproved', 'Tutte le voci di questo mese sono approvate.')}
        </span>
      ) : (
        <>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5 text-destructive hover:text-destructive"
            disabled={selectedCount === 0 || processing}
            onClick={onReject}
          >
            <X className="h-4 w-4" />
            {t('approvals.rejectCount', 'Rifiuta {{entries}}', { entries: entriesLabel(selectedCount) })}
          </Button>
          <Button
            type="button"
            size="sm"
            className="gap-1.5 bg-green-600 text-white hover:bg-green-700"
            disabled={selectedCount === 0 || processing}
            onClick={onApprove}
          >
            {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {t('approvals.approveCount', 'Approva {{entries}}', { entries: entriesLabel(selectedCount) })}
          </Button>
        </>
      )}
    </div>
  );
}
